import {
  SectionStatus,
  OnboardingStatus,
  BusinessSettings,
  GettingStarted,
  TripSettings,
  Simulation,
  FleetSetup,
  OnboardingClients,
  AddOns,
  OverallCompletion,
} from "@/redux/slices/onboarding/types";
import {
  SectionStatusRaw,
  OnboardingStatusRawResponse,
  BusinessSettingsRaw,
  GettingStartedRaw,
  TripSettingsRaw,
  SimulationRaw,
  FleetSetupRaw,
  OnboardingClientsRaw,
  AddOnsRaw,
  OverallCompletionRaw,
} from "./types";

export const transformSectionStatus = (raw: SectionStatusRaw): SectionStatus => ({
  isValid: raw.is_valid,
  isCompleted: raw.is_completed,
  isRequired: raw.is_required,
  completedAt: raw.completed_at,
  completedBy: raw.completed_by,
  lastValidatedAt: raw.last_validated_at,
  validationErrors: raw.validation_errors,
  validationMessage: raw.validation_message,
  metadata: raw.metadata
    ? {
        uuid: raw.metadata.uuid,
        hasMultipleClients: raw.metadata.has_multiple_clients,
      }
    : undefined,
});

export const transformBusinessSettings = (raw: BusinessSettingsRaw): BusinessSettings => ({
  businessDetails: transformSectionStatus(raw.business_details),
  websiteDetails: transformSectionStatus(raw.website_details),
  emailNotificationSettings: transformSectionStatus(raw.email_notification_settings),
  accessControl: transformSectionStatus(raw.access_control),
  displayOptions: transformSectionStatus(raw.display_options),
  isCompleted: raw.is_completed,
});

export const transformGettingStarted = (raw: GettingStartedRaw): GettingStarted => ({
  stripe: transformSectionStatus(raw.stripe),
  generalWalkthrough: transformSectionStatus(raw.general_walkthrough),
  isCompleted: raw.is_completed,
});

export const transformTripSettings = (raw: TripSettingsRaw): TripSettings => ({
  tripCost: transformSectionStatus(raw.trip_cost),
  timeDayMultiplier: transformSectionStatus(raw.time_day_multiplier),
  hourlyHire: transformSectionStatus(raw.hourly_hire),
  transferAndVehicleOptions: transformSectionStatus(raw.transfer_and_vehicle_options),
  unscheduledStops: transformSectionStatus(raw.unscheduled_stops),
  cancellationFees: transformSectionStatus(raw.cancellation_fees),
  billingSettings: transformSectionStatus(raw.billing_settings),
  waitingTimes: transformSectionStatus(raw.waiting_times),
  dispatchSettings: transformSectionStatus(raw.dispatch_settings),
  isCompleted: raw.is_completed,
});

export const transformSimulation = (raw: SimulationRaw): Simulation => ({
  createClient: transformSectionStatus(raw.create_client),
  createDriver: transformSectionStatus(raw.create_driver),
  createVehicle: transformSectionStatus(raw.create_vehicle),
  createBooking: transformSectionStatus(raw.create_booking),
  isCompleted: raw.is_completed,
});

export const transformFleetSetup = (raw: FleetSetupRaw): FleetSetup => ({
  addOwnVehicles: transformSectionStatus(raw.add_own_vehicles),
  addOwnDrivers: transformSectionStatus(raw.add_own_drivers),
  inviteOwnPartnerDrivers: transformSectionStatus(raw.invite_own_partner_drivers),
  isCompleted: raw.is_completed,
});

export const transformOnboardingClients = (raw: OnboardingClientsRaw): OnboardingClients => ({
  registerClient: transformSectionStatus(raw.register_client),
  uploadClients: transformSectionStatus(raw.upload_clients),
  setupCompanyProfile: transformSectionStatus(raw.setup_company_profile),
  isCompleted: raw.is_completed,
});

export const transformAddOns = (raw: AddOnsRaw): AddOns => ({
  setupAddons: transformSectionStatus(raw.setup_addons),
  isCompleted: raw.is_completed,
});

export const transformOverallCompletion = (raw: OverallCompletionRaw): OverallCompletion => ({
  completedSections: raw.completed_sections,
  totalSections: raw.total_sections,
  percentage: raw.percentage,
  validSections: raw.valid_sections,
  validPercentage: raw.valid_percentage,
  isOnboardingComplete: raw.is_onboarding_complete,
});

export const transformOnboardingStatus = (raw: OnboardingStatusRawResponse): OnboardingStatus => ({
  gettingStarted: transformGettingStarted(raw.getting_started),
  businessSettings: transformBusinessSettings(raw.business_settings),
  tripSettings: transformTripSettings(raw.trip_settings),
  simulation: transformSimulation(raw.simulation),
  fleetSetup: transformFleetSetup(raw.fleet_setup),
  onboardingClients: transformOnboardingClients(raw.onboarding_clients),
  addOns: transformAddOns(raw.add_ons),
  overallCompletion: transformOverallCompletion(raw.overall_completion),
});
